import React from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Clock,
  TrendingUp,
  Trophy,
  Target,
  Calendar,
  Brain,
  Award
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Progress } from '../../ui/progress';

interface DashboardStatsProps {
  stats: {
    totalCourses: number;
    activeCourses: number;
    completedCourses: number;
    averageProgress: number;
  };
  enrollments: any[];
}

const ModernDashboardStats: React.FC<DashboardStatsProps> = ({ stats, enrollments }) => {
  const statsCards = [
    {
      title: 'Cours Actifs',
      value: stats.activeCourses,
      icon: BookOpen,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-600 dark:text-blue-400',
      description: 'En cours d\'apprentissage'
    },
    {
      title: 'Progression Moyenne',
      value: `${stats.averageProgress}%`,
      icon: TrendingUp,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-600 dark:text-green-400',
      description: 'De tous vos cours'
    },
    {
      title: 'Cours Terminés',
      value: stats.completedCourses,
      icon: Trophy,
      color: 'from-yellow-500 to-yellow-600',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      textColor: 'text-yellow-600 dark:text-yellow-400',
      description: 'Félicitations !'
    },
    {
      title: 'Total Cours',
      value: stats.totalCourses,
      icon: Target,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      textColor: 'text-purple-600 dark:text-purple-400',
      description: 'Dans votre bibliothèque'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
    >
      {statsCards.map((stat, index) => {
        const Icon = stat.icon;
        
        return (
          <motion.div key={stat.title} variants={cardVariants}>
            <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-6">
                {/* Gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5 group-hover:opacity-10 transition-opacity duration-300`} />
                
                {/* Icon container */}
                <div className={`${stat.bgColor} w-12 h-12 rounded-xl flex items-center justify-center mb-4 relative z-10`}>
                  <Icon className={`h-6 w-6 ${stat.textColor}`} />
                </div>

                {/* Content */}
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {stat.title}
                    </h3>
                  </div>
                  
                  <div className="flex items-baseline space-x-2 mb-2">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">
                      {stat.value}
                    </span>
                  </div>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {stat.description}
                  </p>

                  {/* Progress bar pour certaines stats */}
                  {stat.title === 'Progression Moyenne' && (
                    <div className="mt-3">
                      <Progress 
                        value={stats.averageProgress} 
                        className="h-2"
                      />
                    </div>
                  )}
                </div>

                {/* Decorative element */}
                <div className={`absolute -right-4 -top-4 w-20 h-20 bg-gradient-to-br ${stat.color} opacity-10 rounded-full`} />
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default ModernDashboardStats;
